import { useEffect, useMemo, useRef, useState } from "react";
import FullCalendar from "@fullcalendar/react";
import dayGridPlugin from "@fullcalendar/daygrid";
import timeGridPlugin from "@fullcalendar/timegrid";
import interactionPlugin from "@fullcalendar/interaction";
import listPlugin from "@fullcalendar/list";
import { exportIcs, getAttendees, getCalendarEvents, getGoogleCalendarEvents, importIcs, skipRecurringOccurrence, updatePlannerDate } from "./CalendarService";
import { createItem, deleteItem, getItemById, updateItem, updateItemStatus } from "./planner/PlannerService";
import "./CalendarPage.css";

const TYPE_OPTIONS = ["TODO", "IMPORTANT_DATE", "PAYMENT", "REMINDER"];
const FILTER_TYPE_OPTIONS = [...TYPE_OPTIONS, "GOOGLE"];
const RECURRENCE_OPTIONS = ["NONE", "DAILY", "WEEKLY", "MONTHLY", "YEARLY"];
const CATEGORY_OPTIONS = [
  "WORK", "PERSONAL", "FINANCE", "HEALTH", "FAMILY", "EDUCATION",
  "BUSINESS", "SHOPPING", "TRAVEL", "BILLING", "INVESTMENT",
  "SUBSCRIPTION", "INSURANCE", "HOME", "SOCIAL", "GOALS",
  "OTHER", "PROJECT",
];

export default function CalendarPage() {
  const calendarRef = useRef(null);
  const importInputRef = useRef(null);
  const [events, setEvents] = useState([]);
  const [error, setError] = useState("");
  const [selectedDate, setSelectedDate] = useState("");
  const [range, setRange] = useState({ start: "", end: "" });
  const [activeView, setActiveView] = useState(
    localStorage.getItem("calendar.view") || "timeGridWeek"
  );
  const [filters, setFilters] = useState({
    types: [],
    categories: [],
    includeInactive: false,
    includeRecurring: true,
    query: "",
    includeGoogle: false,
  });
  const [presets, setPresets] = useState(() => {
    try {
      const raw = localStorage.getItem("calendar.presets");
      const parsed = raw ? JSON.parse(raw) : [];
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  });
  const [presetName, setPresetName] = useState("");
  const [selectedPresetId, setSelectedPresetId] = useState("");

  const [detailsOpen, setDetailsOpen] = useState(false);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [detailsError, setDetailsError] = useState("");
  const [detailsEvent, setDetailsEvent] = useState(null);
  const [detailsItem, setDetailsItem] = useState(null);
  const [importBusy, setImportBusy] = useState(false);
  const [detailsOccurrenceDate, setDetailsOccurrenceDate] = useState("");
  const [form, setForm] = useState({
    title: "",
    type: "TODO",
    category: "WORK",
    date: "",
    allDay: true,
    startTime: "",
    endTime: "",
    recurrence: "NONE",
    recurrenceEndDate: "",
    attendeeUserIds: [],
  });
  const [attendees, setAttendees] = useState([]);
  const [attendeesError, setAttendeesError] = useState("");

  const savePresets = (next) => {
    setPresets(next);
    localStorage.setItem("calendar.presets", JSON.stringify(next));
  };


  const fetchEvents = async (startStr, endStr) => {
    setError("");
    try {
      const localPromise = getCalendarEvents(startStr, endStr, {
        types: filters.types,
        categories: filters.categories,
        includeInactive: filters.includeInactive,
        includeRecurring: filters.includeRecurring,
      });

      const googlePromise =
        isOwner() && filters.includeGoogle ? getGoogleCalendarEvents(startStr, endStr) : Promise.resolve([]);

      const [local, google] = await Promise.all([localPromise, googlePromise]);

      const localMapped = (local || []).map((e) => ({
        id: e.id,
        title: e.title,
        start: e.start,
        end: e.end || undefined,
        allDay: e.allDay,
        editable: e.editable,
        eventStartEditable: e.editable,
        eventDurationEditable: e.editable,
        extendedProps: {
          type: e.type,
          category: e.category,
          recurrence: e.recurrence,
          baseId: e.baseId,
          source: "LOCAL",
        },
        ...eventColors(e.type, e.category),
      }));

      const googleMapped = (google || []).map((e) => ({
        id: `gcal-${e.id}`,
        title: e.title || "(No title)",
        start: e.start,
        end: e.end || undefined,
        allDay: Boolean(e.allDay),
        editable: false,
        eventStartEditable: false,
        eventDurationEditable: false,
        extendedProps: {
          type: "GOOGLE",
          category: "OTHER",
          recurrence: "NONE",
          baseId: `gcal-${e.id}`,
          source: "GOOGLE",
        },
        backgroundColor: "#334155",
        borderColor: "#334155",
        textColor: "#ffffff",
      }));

      let merged = [...localMapped, ...googleMapped];
      if (filters.types && filters.types.length) {
        merged = merged.filter((ev) => filters.types.includes(ev.extendedProps?.type));
      }
      if (filters.categories && filters.categories.length) {
        merged = merged.filter((ev) => filters.categories.includes(ev.extendedProps?.category));
      }
      const q = (filters.query || "").trim().toLowerCase();
      setEvents(q ? merged.filter((ev) => (ev.title || "").toLowerCase().includes(q)) : merged);
    } catch {
      setError("Unable to load calendar data.");
    }
  };

  useEffect(() => {
    if (!range.start || !range.end) return;
    fetchEvents(range.start, range.end);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    range.start,
    range.end,
    filters.types,
    filters.categories,
    filters.includeInactive,
    filters.includeRecurring,
    filters.query,
    filters.includeGoogle,
  ]);

  useEffect(() => {
    localStorage.setItem("calendar.view", activeView);
    const api = calendarRef.current?.getApi?.();
    if (api && api.view?.type !== activeView) {
      api.changeView(activeView);
    }
  }, [activeView]);

  const conflicts = useMemo(() => computeConflicts(events), [events]);
  const slotSuggestions = useMemo(() => {
    const base = selectedDate || new Date().toISOString().slice(0, 10);
    return suggestSlots(events, base, 30, 9, 18).slice(0, 5);
  }, [events, selectedDate]);

  const applyPreset = (presetId) => {
    const preset = presets.find((p) => p.id === presetId);
    if (!preset) return;
    setSelectedPresetId(presetId);
    setFilters(preset.filters);
    if (preset.view) setActiveView(preset.view);
  };

  const handleSavePreset = () => {
    const name = presetName.trim();
    if (!name) return;
    const next = [
      ...presets.filter((p) => p.name.toLowerCase() !== name.toLowerCase()),
      {
        id: `preset-${Date.now()}`,
        name,
        view: activeView,
        filters,
      },
    ];
    savePresets(next);
    setPresetName("");
  };

  const handleDeletePreset = () => {
    if (!selectedPresetId) return;
    const next = presets.filter((p) => p.id !== selectedPresetId);
    savePresets(next);
    setSelectedPresetId("");
  };

  const handleExportIcs = async () => {
    if (!range.start || !range.end) return;
    setError("");
    try {
      const blob = await exportIcs(range.start, range.end, {
        types: filters.types.filter((t) => t !== "GOOGLE"),
        categories: filters.categories,
        includeInactive: filters.includeInactive,
        includeRecurring: filters.includeRecurring,
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "lifeos-calendar.ics";
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch {
      setError("Unable to export ICS.");
    }
  };

  const handleImportIcs = async (file) => {
    if (!file) return;
    setError("");
    setImportBusy(true);
    try {
      await importIcs(file, { defaultType: "TODO", defaultCategory: "OTHER" });
      if (range.start && range.end) fetchEvents(range.start, range.end);
    } catch {
      setError("Unable to import ICS.");
    } finally {
      setImportBusy(false);
      if (importInputRef.current) importInputRef.current.value = "";
    }
  };

  const isOwner = () => {
    const auth = JSON.parse(localStorage.getItem("auth") || "{}");
    return auth?.userId === "usr-c25932f5-3093-4ee0-9076-9cd1055fcd55";
  };

  const loadAttendees = () => {
    if (!isOwner()) return;
    getAttendees()
      .then((data) => {
        setAttendees(Array.isArray(data) ? data : []);
      })
      .catch(() => {
        setAttendeesError("Unable to load people list.");
      });
  };

  const handleEventClick = async (clickInfo) => {
    const ev = clickInfo.event;
    setDetailsError("");
    setDetailsEvent(ev);
    setDetailsItem(null);
    setDetailsOpen(true);
    setDetailsOccurrenceDate(ev.id && String(ev.id).includes(":") ? String(ev.id).split(":")[1] : "");

    if (ev.extendedProps?.source !== "LOCAL") return;

    const baseId = ev.extendedProps?.baseId || ev.id;
    if (!baseId) return;

    setDetailsLoading(true);
    try {
      const item = await getItemById(baseId);
      setDetailsItem(item);
    } catch {
      setDetailsError("Unable to load event details.");
    } finally {
      setDetailsLoading(false);
    }
  };

  const handleEventDrop = async (info) => {
    if (info.event.extendedProps.recurrence && info.event.extendedProps.recurrence !== "NONE") {
      info.revert();
      setError("Recurring events cannot be moved.");
      return;
    }
    if (info.event.extendedProps?.source && info.event.extendedProps.source !== "LOCAL") {
      info.revert();
      setError("This event cannot be moved.");
      return;
    }
    const id = info.event.id;
    const date = info.event.startStr.slice(0, 10);
    const allDay = info.event.allDay;
    const startTime = allDay ? null : formatTime(info.event.start);
    const endTime = allDay ? null : formatTime(info.event.end);
    try {
      await updatePlannerDate(id, date, startTime, endTime, allDay);
    } catch {
      info.revert();
      setError("Unable to update date.");
    }
  };

  const handleEventResize = async (info) => {
    if (info.event.extendedProps.recurrence && info.event.extendedProps.recurrence !== "NONE") {
      info.revert();
      setError("Recurring events cannot be resized.");
      return;
    }
    if (info.event.extendedProps?.source && info.event.extendedProps.source !== "LOCAL") {
      info.revert();
      setError("This event cannot be resized.");
      return;
    }
    const id = info.event.id;
    const date = info.event.startStr.slice(0, 10);
    const allDay = info.event.allDay;
    const startTime = allDay ? null : formatTime(info.event.start);
    const endTime = allDay ? null : formatTime(info.event.end);
    try {
      await updatePlannerDate(id, date, startTime, endTime, allDay);
    } catch {
      info.revert();
      setError("Unable to update time.");
    }
  };

  const handleDateClick = (info) => {
    setSelectedDate(info.dateStr);
    setForm((prev) => ({ ...prev, date: info.dateStr }));
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) return;
    const payload = buildPlannerPayload(form);
    try {
      const created = await createItem(payload);
      const createdDate = calendarDateFromItem(created);
      if (createdDate) {
        setEvents((prev) => [
          ...prev,
          {
            id: created.id,
            title: created.title,
            start: calendarStartFromItem(created),
            end: calendarEndFromItem(created),
            allDay: isAllDay(created),
            editable: !created.recurrence || created.recurrence === "NONE",
            extendedProps: {
              type: created.type,
              category: created.category,
              recurrence: created.recurrence,
              baseId: created.id,
            },
            ...eventColors(created.type, created.category),
          },
        ]);
      }
      setForm((prev) => ({ ...prev, title: "" }));
    } catch {
      setError("Unable to create item.");
    }
  };

  const formDateValue = form.date || selectedDate;


  return (
    <div className="calendar-page">
      <div className="calendar-main">
        <div className="calendar-toolbar">
          <h2 className="calendar-title">Calendar</h2>
          <form className="calendar-form-inline" onSubmit={handleCreate}>
            <div className="calendar-field">
              <label className="calendar-label" htmlFor="calendar-title">Title</label>
              <input
                id="calendar-title"
                className="calendar-input"
                placeholder="Add event"
                value={form.title}
                onChange={(e) => setForm((prev) => ({ ...prev, title: e.target.value }))}
              />
            </div>
            <div className="calendar-field">
              <label className="calendar-label" htmlFor="calendar-type">Type</label>
              <select
                id="calendar-type"
                className="calendar-select"
                value={form.type}
                onChange={(e) => setForm((prev) => ({ ...prev, type: e.target.value }))}
              >
                {TYPE_OPTIONS.map((t) => (
                  <option key={t} value={t}>{t}</option>
                ))}
              </select>
            </div>
            <div className="calendar-field">
              <label className="calendar-label" htmlFor="calendar-category">Category</label>
              <select
                id="calendar-category"
                className="calendar-select"
                value={form.category}
                onChange={(e) => setForm((prev) => ({ ...prev, category: e.target.value }))}
              >
                {CATEGORY_OPTIONS.map((c) => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
            </div>
            {isOwner() && (
              <div className="calendar-field calendar-field-span">
                <label className="calendar-label">Invite people</label>
                <div className="calendar-attendees">
                  {attendees.length === 0 && !attendeesError && (
                    <button
                      type="button"
                      className="calendar-btn calendar-btn-secondary"
                      onClick={loadAttendees}
                    >
                      Load people
                    </button>
                  )}
                  {attendeesError && <div className="calendar-muted">{attendeesError}</div>}
                  {attendees.map((person) => (
                    <label key={person.id} className="calendar-attendee-item">
                      <input
                        type="checkbox"
                        checked={form.attendeeUserIds.includes(person.id)}
                        onChange={(e) => {
                          const checked = e.target.checked;
                          setForm((prev) => ({
                            ...prev,
                            attendeeUserIds: checked
                              ? [...prev.attendeeUserIds, person.id]
                              : prev.attendeeUserIds.filter((id) => id !== person.id),
                          }));
                        }}
                      />
                      <span>{person.firstName} {person.lastName}</span>
                    </label>
                  ))}
                </div>
              </div>
            )}
            <div className="calendar-field">
              <label className="calendar-label" htmlFor="calendar-date">Date</label>
              <input
                id="calendar-date"
                className="calendar-input"
                type="date"
                value={formDateValue}
                onChange={(e) => setForm((prev) => ({ ...prev, date: e.target.value }))}
              />
            </div>
            <div className="calendar-field calendar-field-inline">
              <label className="calendar-label" htmlFor="calendar-allday">All day</label>
              <label className="calendar-inline-toggle">
                <input
                  id="calendar-allday"
                  type="checkbox"
                  checked={form.allDay}
                  onChange={(e) => {
                    const checked = e.target.checked;
                    setForm((prev) => ({
                      ...prev,
                      allDay: checked,
                      startTime: checked ? "" : prev.startTime,
                      endTime: checked ? "" : prev.endTime,
                    }));
                  }}
                />
                <span>Enabled</span>
              </label>
            </div>
            <div className="calendar-field">
              <label className="calendar-label" htmlFor="calendar-start">Start time</label>
              <input
                id="calendar-start"
                className="calendar-input"
                type="time"
                value={form.startTime}
                onFocus={() =>
                  setForm((prev) => ({
                    ...prev,
                    allDay: false,
                  }))
                }
                onChange={(e) =>
                  setForm((prev) => ({
                    ...prev,
                    startTime: e.target.value,
                    allDay: e.target.value ? false : prev.allDay,
                  }))
                }
              />
            </div>
            <div className="calendar-field">
              <label className="calendar-label" htmlFor="calendar-end">End time</label>
              <input
                id="calendar-end"
                className="calendar-input"
                type="time"
                value={form.endTime}
                onFocus={() =>
                  setForm((prev) => ({
                    ...prev,
                    allDay: false,
                  }))
                }
                onChange={(e) =>
                  setForm((prev) => ({
                    ...prev,
                    endTime: e.target.value,
                    allDay: e.target.value ? false : prev.allDay,
                  }))
                }
              />
            </div>
            <div className="calendar-field">
              <label className="calendar-label" htmlFor="calendar-recurrence">Recurrence</label>
              <select
                id="calendar-recurrence"
                className="calendar-select"
                value={form.recurrence}
                onChange={(e) => setForm((prev) => ({ ...prev, recurrence: e.target.value }))}
              >
                {RECURRENCE_OPTIONS.map((r) => (
                  <option key={r} value={r}>{r}</option>
                ))}
              </select>
            </div>
            <div className="calendar-field">
              <label className="calendar-label" htmlFor="calendar-repeat">Repeat until</label>
              <input
                id="calendar-repeat"
                className="calendar-input"
                type="date"
                disabled={form.recurrence === "NONE"}
                value={form.recurrenceEndDate}
                onChange={(e) => setForm((prev) => ({ ...prev, recurrenceEndDate: e.target.value }))}
              />
            </div>
            <div className="calendar-field calendar-field-action">
              <label className="calendar-label calendar-label-empty">Action</label>
              <button className="calendar-btn" type="submit">Create</button>
            </div>
          </form>
        </div>

        <div className="calendar-filters" role="region" aria-label="Calendar filters">
          <div className="calendar-filter-grid">
            <div className="calendar-field">
              <label className="calendar-label" htmlFor="calendar-filter-view">View</label>
              <select
                id="calendar-filter-view"
                className="calendar-select"
                value={activeView}
                onChange={(e) => setActiveView(e.target.value)}
              >
                <option value="timeGridWeek">Week</option>
                <option value="dayGridMonth">Month</option>
                <option value="listWeek">Agenda</option>
              </select>
            </div>

            <div className="calendar-field">
              <label className="calendar-label" htmlFor="calendar-filter-presets">Preset</label>
              <select
                id="calendar-filter-presets"
                className="calendar-select"
                value={selectedPresetId}
                onChange={(e) => applyPreset(e.target.value)}
              >
                <option value="">(None)</option>
                {presets.map((p) => (
                  <option key={p.id} value={p.id}>{p.name}</option>
                ))}
              </select>
            </div>

            <div className="calendar-field">
              <label className="calendar-label" htmlFor="calendar-filter-presetname">Save Preset</label>
              <input
                id="calendar-filter-presetname"
                className="calendar-input"
                placeholder="Preset name"
                value={presetName}
                onChange={(e) => setPresetName(e.target.value)}
              />
            </div>

            <div className="calendar-field calendar-field-action">
              <label className="calendar-label calendar-label-empty">Action</label>
              <button className="calendar-btn" type="button" onClick={handleSavePreset}>
                Save
              </button>
            </div>

            <div className="calendar-field calendar-field-action">
              <label className="calendar-label calendar-label-empty">Action</label>
              <button className="calendar-btn calendar-btn-secondary" type="button" onClick={handleDeletePreset}>
                Delete preset
              </button>
            </div>

            <div className="calendar-field">
              <label className="calendar-label" htmlFor="calendar-filter-q">Search</label>
              <input
                id="calendar-filter-q"
                className="calendar-input"
                placeholder="Filter by title"
                value={filters.query}
                onChange={(e) => setFilters((prev) => ({ ...prev, query: e.target.value }))}
              />
            </div>

            <div className="calendar-field">
              <label className="calendar-label" htmlFor="calendar-filter-types">Types</label>
              <select
                id="calendar-filter-types"
                className="calendar-select calendar-select-multi"
                multiple
                value={filters.types}
                onChange={(e) => {
                  const values = Array.from(e.target.selectedOptions).map((o) => o.value);
                  setFilters((prev) => ({ ...prev, types: values }));
                }}
              >
                {FILTER_TYPE_OPTIONS.map((t) => (
                  <option key={t} value={t}>{t}</option>
                ))}
              </select>
            </div>

            <div className="calendar-field">
              <label className="calendar-label" htmlFor="calendar-filter-categories">Categories</label>
              <select
                id="calendar-filter-categories"
                className="calendar-select calendar-select-multi"
                multiple
                value={filters.categories}
                onChange={(e) => {
                  const values = Array.from(e.target.selectedOptions).map((o) => o.value);
                  setFilters((prev) => ({ ...prev, categories: values }));
                }}
              >
                {CATEGORY_OPTIONS.map((c) => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
            </div>

            <div className="calendar-field calendar-field-inline">
              <label className="calendar-label">Recurring</label>
              <label className="calendar-inline-toggle">
                <input
                  type="checkbox"
                  checked={filters.includeRecurring}
                  onChange={(e) => setFilters((prev) => ({ ...prev, includeRecurring: e.target.checked }))}
                />
                <span>Show recurring</span>
              </label>
            </div>

            <div className="calendar-field calendar-field-inline">
              <label className="calendar-label">Status</label>
              <label className="calendar-inline-toggle">
                <input
                  type="checkbox"
                  checked={filters.includeInactive}
                  onChange={(e) => setFilters((prev) => ({ ...prev, includeInactive: e.target.checked }))}
                />
                <span>Include done</span>
              </label>
            </div>

            {isOwner() && (
              <div className="calendar-field calendar-field-inline">
                <label className="calendar-label">Google</label>
                <label className="calendar-inline-toggle">
                  <input
                    type="checkbox"
                    checked={filters.includeGoogle}
                    onChange={(e) => setFilters((prev) => ({ ...prev, includeGoogle: e.target.checked }))}
                  />
                  <span>Show Google</span>
                </label>
              </div>
            )}

            <div className="calendar-field calendar-field-action">
              <label className="calendar-label calendar-label-empty">Action</label>
              <button
                type="button"
                className="calendar-btn calendar-btn-secondary"
                onClick={() =>
                  setFilters({
                    types: [],
                    categories: [],
                    includeInactive: false,
                    includeRecurring: true,
                    query: "",
                    includeGoogle: false,
                  })
                }
              >
                Clear filters
              </button>
            </div>

            <div className="calendar-field calendar-field-action">
              <label className="calendar-label calendar-label-empty">Action</label>
              <button type="button" className="calendar-btn" onClick={handleExportIcs} disabled={!range.start || !range.end}>
                Export ICS
              </button>
            </div>

            <div className="calendar-field calendar-field-action">
              <label className="calendar-label calendar-label-empty">Action</label>
              <button
                type="button"
                className="calendar-btn calendar-btn-secondary"
                onClick={() => importInputRef.current?.click()}
                disabled={importBusy}
              >
                {importBusy ? "Importing…" : "Import ICS"}
              </button>
              <input
                ref={importInputRef}
                type="file"
                accept=".ics,text/calendar"
                className="calendar-file-input"
                onChange={(e) => handleImportIcs(e.target.files?.[0])}
              />
            </div>
          </div>
          <div className="calendar-muted calendar-filter-hint">
            Tip: hold Cmd/Ctrl to multi-select.
          </div>
          {conflicts.size > 0 && (
            <div className="calendar-muted calendar-conflict-hint">
              {conflicts.size} conflict{conflicts.size === 1 ? "" : "s"} detected in this range.
            </div>
          )}
          {slotSuggestions.length > 0 && (
            <div className="calendar-muted calendar-slot-hint">
              Suggested free slots ({selectedDate || "today"}): {slotSuggestions.join(", ")}
            </div>
          )}
        </div>

        {error && <div className="calendar-muted">{error}</div>}
        <FullCalendar
          ref={calendarRef}
          plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin, listPlugin]}
          initialView={activeView}
          headerToolbar={{
            left: "prev,next today",
            center: "title",
            right: "timeGridWeek,dayGridMonth,listWeek",
          }}
          height="auto"
          eventStartEditable
          eventDurationEditable
          editable
          selectable
          events={events}
          eventClassNames={(arg) => (arg.event.extendedProps?.conflict ? ["calendar-event-conflict"] : [])}
          datesSet={(info) => {
            const start = info.startStr.slice(0, 10);
            const end = info.endStr.slice(0, 10);
            setRange({ start, end });
            fetchEvents(start, end);
          }}
          eventDrop={handleEventDrop}
          eventResize={handleEventResize}
          eventClick={handleEventClick}
          dateClick={handleDateClick}
        />
      </div>

      {detailsOpen && (
        <div className="calendar-modal-backdrop" role="dialog" aria-modal="true">
          <div className="calendar-modal">
            <div className="calendar-modal-header">
              <div className="calendar-modal-title">Event</div>
              <button className="calendar-icon-btn" type="button" onClick={() => setDetailsOpen(false)} aria-label="Close">
                ×
              </button>
            </div>

            {detailsError && <div className="calendar-muted">{detailsError}</div>}
            {detailsLoading && <div className="calendar-muted">Loading…</div>}

            {!detailsLoading && detailsEvent && detailsEvent.extendedProps?.source !== "LOCAL" && (
              <div className="calendar-modal-body">
                <div className="calendar-muted">Read-only event.</div>
                <div className="calendar-detail-row"><span className="calendar-detail-k">Title</span><span>{detailsEvent.title}</span></div>
                <div className="calendar-detail-row"><span className="calendar-detail-k">Start</span><span>{detailsEvent.startStr}</span></div>
                {detailsEvent.endStr && <div className="calendar-detail-row"><span className="calendar-detail-k">End</span><span>{detailsEvent.endStr}</span></div>}
              </div>
            )}

            {!detailsLoading && detailsItem && (
              <CalendarDetailsEditor
                item={detailsItem}
                onChange={setDetailsItem}
                occurrenceDate={detailsOccurrenceDate}
                onSave={async () => {
                  setDetailsError("");
                  try {
                    const updated = await updateItem(detailsItem.id, detailsItem);
                    setDetailsItem(updated);
                    if (range.start && range.end) fetchEvents(range.start, range.end);
                    setDetailsOpen(false);
                  } catch {
                    setDetailsError("Unable to save changes.");
                  }
                }}
                onDelete={async () => {
                  setDetailsError("");
                  try {
                    await deleteItem(detailsItem.id);
                    if (range.start && range.end) fetchEvents(range.start, range.end);
                    setDetailsOpen(false);
                  } catch {
                    setDetailsError("Unable to delete.");
                  }
                }}
                onMarkDone={async () => {
                  setDetailsError("");
                  try {
                    await updateItemStatus(detailsItem.id, "DONE");
                    if (range.start && range.end) fetchEvents(range.start, range.end);
                    setDetailsOpen(false);
                  } catch {
                    setDetailsError("Unable to update status.");
                  }
                }}
                onSkipOccurrence={async (date) => {
                  setDetailsError("");
                  try {
                    await skipRecurringOccurrence(detailsItem.id, date);
                    if (range.start && range.end) fetchEvents(range.start, range.end);
                    setDetailsOpen(false);
                  } catch {
                    setDetailsError("Unable to skip occurrence.");
                  }
                }}
              />
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function CalendarDetailsEditor({ item, onChange, onSave, onDelete, onMarkDone, occurrenceDate, onSkipOccurrence }) {
  const dateValue = item.type === "IMPORTANT_DATE" ? item.eventDate : item.type === "PAYMENT" ? item.paymentDate : item.dueDate;
  const allDay = !item.startTime && !item.endTime;
  const isRecurring = (item.recurrence || "NONE") !== "NONE";

  return (
    <div className="calendar-modal-body">
      <div className="calendar-modal-grid">
        <div className="calendar-field calendar-field-span">
          <label className="calendar-label" htmlFor="cal-edit-title">Title</label>
          <input
            id="cal-edit-title"
            className="calendar-input"
            value={item.title || ""}
            onChange={(e) => onChange({ ...item, title: e.target.value })}
          />
        </div>

        <div className="calendar-field calendar-field-span">
          <label className="calendar-label" htmlFor="cal-edit-desc">Description</label>
          <textarea
            id="cal-edit-desc"
            className="calendar-input calendar-textarea"
            value={item.description || ""}
            onChange={(e) => onChange({ ...item, description: e.target.value })}
          />
        </div>

        <div className="calendar-field">
          <label className="calendar-label" htmlFor="cal-edit-type">Type</label>
          <select
            id="cal-edit-type"
            className="calendar-select"
            value={item.type || "TODO"}
            onChange={(e) => onChange({ ...item, type: e.target.value })}
          >
            {TYPE_OPTIONS.map((t) => (
              <option key={t} value={t}>{t}</option>
            ))}
          </select>
        </div>

        <div className="calendar-field">
          <label className="calendar-label" htmlFor="cal-edit-category">Category</label>
          <select
            id="cal-edit-category"
            className="calendar-select"
            value={item.category || "WORK"}
            onChange={(e) => onChange({ ...item, category: e.target.value })}
          >
            {CATEGORY_OPTIONS.map((c) => (
              <option key={c} value={c}>{c}</option>
            ))}
          </select>
        </div>

        <div className="calendar-field">
          <label className="calendar-label" htmlFor="cal-edit-date">Date</label>
          <input
            id="cal-edit-date"
            className="calendar-input"
            type="date"
            value={dateValue || ""}
            onChange={(e) => {
              const v = e.target.value;
              if (item.type === "IMPORTANT_DATE") {
                onChange({ ...item, eventDate: v, dueDate: null, paymentDate: null });
              } else if (item.type === "PAYMENT") {
                onChange({ ...item, paymentDate: v, dueDate: null, eventDate: null });
              } else {
                onChange({ ...item, dueDate: v, eventDate: null, paymentDate: null });
              }
            }}
          />
        </div>

        <div className="calendar-field calendar-field-inline">
          <label className="calendar-label">All day</label>
          <label className="calendar-inline-toggle">
            <input
              type="checkbox"
              checked={allDay}
              onChange={(e) => {
                const checked = e.target.checked;
                onChange({
                  ...item,
                  startTime: checked ? null : item.startTime || "09:00:00",
                  endTime: checked ? null : item.endTime || "09:30:00",
                });
              }}
            />
            <span>Enabled</span>
          </label>
        </div>

        <div className="calendar-field">
          <label className="calendar-label" htmlFor="cal-edit-start">Start</label>
          <input
            id="cal-edit-start"
            className="calendar-input"
            type="time"
            value={(item.startTime || "").slice(0, 5)}
            disabled={allDay}
            onChange={(e) => onChange({ ...item, startTime: normalizeTime(e.target.value) })}
          />
        </div>

        <div className="calendar-field">
          <label className="calendar-label" htmlFor="cal-edit-end">End</label>
          <input
            id="cal-edit-end"
            className="calendar-input"
            type="time"
            value={(item.endTime || "").slice(0, 5)}
            disabled={allDay}
            onChange={(e) => onChange({ ...item, endTime: normalizeTime(e.target.value) })}
          />
        </div>

        <div className="calendar-field">
          <label className="calendar-label" htmlFor="cal-edit-recur">Recurrence</label>
          <select
            id="cal-edit-recur"
            className="calendar-select"
            value={item.recurrence || "NONE"}
            onChange={(e) => onChange({ ...item, recurrence: e.target.value })}
          >
            {RECURRENCE_OPTIONS.map((r) => (
              <option key={r} value={r}>{r}</option>
            ))}
          </select>
        </div>

        <div className="calendar-field">
          <label className="calendar-label" htmlFor="cal-edit-recurend">Repeat until</label>
          <input
            id="cal-edit-recurend"
            className="calendar-input"
            type="date"
            disabled={(item.recurrence || "NONE") === "NONE"}
            value={item.recurrenceEndDate || ""}
            onChange={(e) => onChange({ ...item, recurrenceEndDate: e.target.value || null })}
          />
        </div>

        <div className="calendar-field calendar-field-inline">
          <label className="calendar-label">Reminder</label>
          <label className="calendar-inline-toggle">
            <input
              type="checkbox"
              checked={Boolean(item.reminderEnabled)}
              onChange={(e) => onChange({ ...item, reminderEnabled: e.target.checked })}
            />
            <span>Enabled</span>
          </label>
        </div>

        <div className="calendar-field">
          <label className="calendar-label" htmlFor="cal-edit-reminddays">Remind (days before)</label>
          <input
            id="cal-edit-reminddays"
            className="calendar-input"
            type="number"
            min="0"
            value={item.reminderBeforeDays ?? 0}
            disabled={!item.reminderEnabled}
            onChange={(e) => onChange({ ...item, reminderBeforeDays: Number(e.target.value) })}
          />
        </div>

        <div className="calendar-field">
          <label className="calendar-label" htmlFor="cal-edit-status">Status</label>
          <select
            id="cal-edit-status"
            className="calendar-select"
            value={item.status || "ACTIVE"}
            onChange={(e) => onChange({ ...item, status: e.target.value })}
          >
            <option value="ACTIVE">ACTIVE</option>
            <option value="DONE">DONE</option>
            <option value="SKIPPED">SKIPPED</option>
            <option value="CANCELLED">CANCELLED</option>
            <option value="TRACK">TRACK</option>
          </select>
        </div>
      </div>

      <div className="calendar-modal-actions">
        <button className="calendar-btn" type="button" onClick={onSave}>Save</button>
        {isRecurring && occurrenceDate && (
          <button
            className="calendar-btn calendar-btn-secondary"
            type="button"
            onClick={() => onSkipOccurrence?.(occurrenceDate)}
          >
            Skip occurrence
          </button>
        )}
        <button className="calendar-btn calendar-btn-secondary" type="button" onClick={onMarkDone}>Mark done</button>
        <button className="calendar-btn calendar-btn-danger" type="button" onClick={onDelete}>Delete</button>
      </div>
    </div>
  );
}

function computeConflicts(events) {
  const conflicts = new Set();
  const timed = (events || []).filter((e) => !e.allDay && e.start && e.end);
  timed.sort((a, b) => new Date(a.start).getTime() - new Date(b.start).getTime());
  for (let i = 0; i < timed.length; i += 1) {
    const a = timed[i];
    const aStart = new Date(a.start).getTime();
    const aEnd = new Date(a.end).getTime();
    for (let j = i + 1; j < timed.length; j += 1) {
      const b = timed[j];
      const bStart = new Date(b.start).getTime();
      const bEnd = new Date(b.end).getTime();
      if (bStart >= aEnd) break;
      if (bEnd > aStart) {
        conflicts.add(a.id);
        conflicts.add(b.id);
      }
    }
  }
  return conflicts;
}

function suggestSlots(events, dateStr, durationMins = 30, workStartHour = 9, workEndHour = 18) {
  const dayStart = new Date(`${dateStr}T${String(workStartHour).padStart(2, "0")}:00:00`);
  const dayEnd = new Date(`${dateStr}T${String(workEndHour).padStart(2, "0")}:00:00`);
  if (Number.isNaN(dayStart.getTime()) || Number.isNaN(dayEnd.getTime())) return [];

  const busy = (events || [])
    .filter((e) => !e.allDay && e.start && e.end && String(e.start).startsWith(dateStr))
    .map((e) => [new Date(e.start).getTime(), new Date(e.end).getTime()])
    .filter(([s, e]) => !Number.isNaN(s) && !Number.isNaN(e) && e > s)
    .sort((a, b) => a[0] - b[0]);

  const merged = [];
  for (const [s, e] of busy) {
    if (!merged.length || s > merged[merged.length - 1][1]) {
      merged.push([s, e]);
    } else {
      merged[merged.length - 1][1] = Math.max(merged[merged.length - 1][1], e);
    }
  }

  const slots = [];
  let cursor = dayStart.getTime();
  const endLimit = dayEnd.getTime();
  const durMs = durationMins * 60 * 1000;

  for (const [s, e] of merged) {
    if (cursor + durMs <= s) {
      slots.push(formatSlot(cursor, cursor + durMs));
      if (slots.length >= 10) return slots;
    }
    cursor = Math.max(cursor, e);
    if (cursor >= endLimit) break;
  }
  if (cursor + durMs <= endLimit) {
    slots.push(formatSlot(cursor, cursor + durMs));
  }
  return slots;
}

function formatSlot(startMs, endMs) {
  const s = new Date(startMs);
  const e = new Date(endMs);
  const pad = (n) => String(n).padStart(2, "0");
  return `${pad(s.getHours())}:${pad(s.getMinutes())}-${pad(e.getHours())}:${pad(e.getMinutes())}`;
}

function buildPlannerPayload(form) {
  const hasTime = Boolean(form.startTime || form.endTime);
  const isAllDay = form.allDay && !hasTime;
  const normalizedStart = normalizeTime(form.startTime);
  const normalizedEnd = normalizeTime(form.endTime);
  const base = {
    title: form.title,
    type: form.type,
    category: form.category,
    priority: "MEDIUM",
    recurrence: form.recurrence || "NONE",
    recurrenceEndDate: form.recurrence !== "NONE" ? form.recurrenceEndDate || null : null,
    attendeeUserIds: form.attendeeUserIds || [],
  };
  const reminder =
    form.type === "REMINDER"
      ? { reminderEnabled: true, reminderBeforeDays: 0 }
      : {};
  const timeFields = isAllDay
    ? {}
    : {
        startTime: normalizedStart,
        endTime: normalizedEnd,
      };
  if (!form.date) {
    return { ...base, ...reminder, ...timeFields };
  }
  if (form.type === "IMPORTANT_DATE") {
    return { ...base, ...reminder, ...timeFields, eventDate: form.date };
  }
  if (form.type === "PAYMENT") {
    return { ...base, ...reminder, ...timeFields, paymentDate: form.date };
  }
  return { ...base, ...reminder, ...timeFields, dueDate: form.date };
}

function calendarDateFromItem(item) {
  if (item.type === "IMPORTANT_DATE") return item.eventDate;
  if (item.type === "PAYMENT") return item.paymentDate;
  return item.dueDate;
}

function isAllDay(item) {
  return !item.startTime && !item.endTime;
}

function calendarStartFromItem(item) {
  const date = calendarDateFromItem(item);
  if (!date) return null;
  if (item.startTime) {
    return `${date}T${item.startTime}`;
  }
  return date;
}

function calendarEndFromItem(item) {
  const date = calendarDateFromItem(item);
  if (!date || !item.endTime) return undefined;
  return `${date}T${item.endTime}`;
}

function formatTime(date) {
  if (!date) return null;
  const hours = String(date.getHours()).padStart(2, "0");
  const minutes = String(date.getMinutes()).padStart(2, "0");
  return `${hours}:${minutes}`;
}

function normalizeTime(value) {
  if (!value) return null;
  return value.length === 5 ? `${value}:00` : value;
}

function eventColors(type, category) {
  const categoryPalette = {
    WORK: "#1f4acc",
    BUSINESS: "#1f4acc",
    PROJECT: "#1f4acc",
    FINANCE: "#1a6f3b",
    BILLING: "#1a6f3b",
    INVESTMENT: "#1a6f3b",
    INSURANCE: "#1a6f3b",
    SUBSCRIPTION: "#1a6f3b",
    HEALTH: "#0f766e",
    FAMILY: "#b45309",
    PERSONAL: "#2563eb",
    TRAVEL: "#7c3aed",
    SOCIAL: "#be123c",
    OTHER: "#374151",
  };
  const typePalette = {
    TODO: "#0f766e",
    REMINDER: "#b45309",
    IMPORTANT_DATE: "#b91c1c",
    PAYMENT: "#166534",
  };
  const color = categoryPalette[category] || typePalette[type] || "#374151";
  return {
    backgroundColor: color,
    borderColor: color,
    textColor: "#ffffff",
  };
}
